import { useEffect, useState, useRef } from "react";
import toast from "react-hot-toast";
import { useLoaderData } from "react-router-dom";
import "./CreateBerth.css";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import { Typography } from "@mui/material";
import { Box } from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import defaultBlogImage from "../../../../../assets/default-blog.png";
import { BerthLocationModal, BerthCard, CardSkeleton, BerthDateSlotModal } from "../../../../../components";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import { createBerth, getBerthsByUserId, updateBerthById, uploadBerthImage } from "../../../../../services";
import DateRangeIcon from "@mui/icons-material/DateRange";
import dayjs from "dayjs";
import { useUser } from "../../../../../contexts/UserContext";
import { trackBerthListingCreated } from "../../../../../utils/analytics";

const powers = [
  "No Power Required",
  "16 Amp Single Phase",
  "30 Amp Service",
  "32 Amp Single Phase",
  "50 Amp Service",
  "63 Amp 3-Phase",
  "63 Amp Single Phase",
  "100 Amp Single Phase",
  "125 Amp 3-Phase",
  "480V 3-Phase 100 amp",
  "480V 3-Phase 200 amp",
  "Hard wire 480V 3-Phase 600amp",
  "Other , please specify"
];

export const CreateBerth = () => {
  const baseUrl = process.env.REACT_APP_API_URL;
  const user = useLoaderData();
  const { profileComplete, isStripeAccountVerified } = useUser();

  const imageRef = useRef(null);
  const [berths, setBerths] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [dateModalMode, setDateModalMode] = useState("availability")
  const [values, setValues] = useState({ chargeType: 'day' });
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  // Handles the "Other" option for power field
  const [userSpecifiedPower, setUserSpecifiedPower] = useState("");
  const [showOtherPowerTextField, setShowOtherPowerTextField] = useState(false);

  const fileTypes = ["image/avif", "image/webp"];
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file === undefined) {
      return;
    }

    if (fileTypes.includes(file.type)) {
      toast.error("File type not allowed");
      return;
    }

    const url = URL.createObjectURL(file);
    setImage(url);
    setImageFile(file);
    return;
  };

  const getBerths = async () => {
    const res = await getBerthsByUserId(user?.data?.id, user?.token);

    if (res?.status === true) {
      setBerths(res?.data?.sort((a, b) => a?.id - b?.id));
      return;
    }

    if (res?.status === false) {
      if (res?.message === "Invalid or expired access token.") {
        toast.error("Token expired, please re-login and try");
        setBerths([]);
        return;
      }

      toast.error("Something went wrong please try later");
      setBerths([]);
      return;
    }

    toast.error("Something went wrong please try later");
    setBerths([]);
    return;
  };

  useEffect(() => {
    getBerths();
  }, []);

  const setValue = (target) => {
    const { name, value } = target;
    setValues({ ...values, [name]: value });

    if (name === "power") {
      if (value === "Other , please specify") {
        setShowOtherPowerTextField(true);
      } else {
        setShowOtherPowerTextField(false);
        setUserSpecifiedPower("");
      }
    }
    return;
  };

  const handleSelectedPlace = (e, dragCoord) => {
    const location = e.features[0].properties;
    const { place_formatted, name, coordinates } = location;

    const data = {
      currentLatitude:
        dragCoord === null ? coordinates.latitude : dragCoord?.lat.toFixed(5),
      currentlongitude:
        dragCoord === null ? coordinates.longitude : dragCoord?.lng.toFixed(5),
      currentLocation: { name, place_formatted },
    };

    setValues({ ...values, ...data });
    setOpen(false);
    return;
  };

  const handleDateSlotSelection = (startDate, endDate) => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    if (end.isBefore(start)) {
      toast.error("End date cannot be before start date, changes are not made in date range!");
      return;
    }

    // Update only the relevant date fields based on the modal mode
    if (dateModalMode === "availability") {
      setValues({
        ...values,
        berthAvailableDate: start.format("YYYY-MM-DD"),
        berthAvailableUntil: end.format("YYYY-MM-DD")
      });
    } else if (dateModalMode === "fixedPrice") {
      setValues({
        ...values,
        fixedPeriodStartDate: start.format("YYYY-MM-DD"),
        fixedPeriodEndDate: end.format("YYYY-MM-DD")
      });
    }
    return;
  };

  const handleRateTypeChange = (type) => {
    // Check if switching to "month" and available dates are too short
    if (type === "month" && values.berthAvailableDate && values.berthAvailableUntil) {
      const startDate = dayjs(values.berthAvailableDate);
      const endDate = dayjs(values.berthAvailableUntil);
      const duration = endDate.diff(startDate, 'day') + 1;

      if (duration < 30) {
        toast.error("Available dates period is too short for monthly pricing. Monthly bookings require at least 30 days. Please update your availability dates.");
        // Don't prevent the change, but show warning
      }
    }

    // Get the rate value for the selected pricing type (if it exists)
    let rateForSelectedType = "";
    switch (type) {
      case "day":
        rateForSelectedType = values.chargesPerDay || "";
        break;
      case "month":
        rateForSelectedType = values.chargesPerMonth || "";
        break;
      case "fixed":
        rateForSelectedType = values.fixedTotalPrice || "";
        break;
      default:
        rateForSelectedType = "";
    }

    // Update chargeType and set rate to the value for selected type
    // All pricing fields (chargesPerDay, chargesPerMonth, fixedTotalPrice) are preserved
    setValues((prev) => ({
      ...prev,
      chargeType: type,
      rate: rateForSelectedType,
    }));
    return;
  }

  const handleRate = (e) => {
    const rateObject = {
      rate: e.target.value
    }

    // Update only the specific pricing field based on chargeType
    switch (values.chargeType) {
      case "day":
        rateObject.chargesPerDay = e.target.value;
        break;

      case "month":
        rateObject.chargesPerMonth = e.target.value;
        break;

      case "fixed":
        rateObject.fixedTotalPrice = e.target.value;
        break;

      default:
        break;
    }

    // Preserve all existing pricing fields
    setValues({ ...values, ...rateObject });
    return;
  }

  // console.log("values: ", values);

  const uploadImage = async (id) => {
    if (imageFile === null) {
      getBerths();
      return;
    }

    setLoading(true);
    const res = await uploadBerthImage(imageFile, id, user?.token);

    if (res?.status === true) {
      toast.success("Berth image uploaded successfully");
      setLoading(false);
      await getBerths();
      return;
    }

    if (res?.status === false) {
      if (res?.message === "Invalid or expired access token.") {
        toast.error("Token expired, please re-login and try");
        setLoading(false);
        await getBerths();
        return;
      }

      toast.error("Error! Upload berth image later when updating.");
      setLoading(false);
      await getBerths();
      return;
    }

    toast.error("Error! Upload berth image later when updating.");
    setLoading(false);
    await getBerths();
    return;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if(!profileComplete || !isStripeAccountVerified) {
      const toastMsg = !profileComplete ?
        "Please complete your profile first"
        : "Please connect your Stripe account first";

      toast.error(toastMsg);
      return;
    }

    if (!values?.currentLatitude) {
      toast.error("Please select berth location");
      return;
    }

    const isSale = values?.availableFor === "sale";

    // Validation for Sale type
    if (isSale) {
      if (!values?.availableFromDate) {
        toast.error("Please select 'Available from' date");
        return;
      }
      if (!values?.fixedTotalPrice || values?.fixedTotalPrice <= 0) {
        toast.error("Please enter the sale price");
        return;
      }
    } else {
      // Validation for Hire/Swap/Both types
      if (!values?.berthAvailableDate) {
        toast.error("Please select slot of availability");
        return;
      }

      // Check if at least one pricing type has a value greater than 0
      if ((!values?.chargesPerDay || values?.chargesPerDay <= 0) &&
          (!values?.chargesPerMonth || values?.chargesPerMonth <= 0) &&
          (!values?.fixedTotalPrice || values?.fixedTotalPrice <= 0)) {
        toast.error("Please enter at least one rate value greater than 0");
        return;
      }

      // Validate fixed rate period if fixedTotalPrice is set
      if (values.fixedTotalPrice && values.fixedTotalPrice > 0 &&
          (!values.fixedPeriodStartDate || !values.fixedPeriodEndDate)) {
        toast.error("Please select duration of fixed rate period");
        return;
      }

      // Validate monthly pricing date requirements if chargesPerMonth is set
      if (values.chargesPerMonth && values.chargesPerMonth > 0 &&
          values.berthAvailableDate && values.berthAvailableUntil) {
        const startDate = dayjs(values.berthAvailableDate);
        const endDate = dayjs(values.berthAvailableUntil);
        const duration = endDate.diff(startDate, 'day') + 1;

        if (duration < 30) {
          toast.error("Monthly pricing requires at least 30 days. Please update your availability dates or remove monthly pricing.");
          return;
        }
      }
    }

    const { length, beam, depth, dockHeight, chargeType, rate, ...rest } = values;
    const berthSpecifications = { length, beam, depth, dockHeight };
    rest.berthSpecifications = berthSpecifications;
    rest.userId = user?.data?.id;

    // Use custom power value if "Other" was selected
    if (showOtherPowerTextField && userSpecifiedPower) {
      rest.power = userSpecifiedPower;
    }

    // Convert 0 pricing values to null
    if (!rest.chargesPerDay || rest.chargesPerDay <= 0) {
      rest.chargesPerDay = null;
    }
    if (!rest.chargesPerMonth || rest.chargesPerMonth <= 0) {
      rest.chargesPerMonth = null;
    }
    if (!rest.fixedTotalPrice || rest.fixedTotalPrice <= 0) {
      rest.fixedTotalPrice = null;
    }

    // Only include fixed period dates if fixedTotalPrice has value
    if (rest.fixedTotalPrice && rest.fixedTotalPrice > 0) {
      rest.fixedPeriodStartDate = values.fixedPeriodStartDate;
      rest.fixedPeriodEndDate = values.fixedPeriodEndDate;
    } else {
      // Remove fixed period dates if fixedTotalPrice is not set
      rest.fixedPeriodStartDate = null;
      rest.fixedPeriodEndDate = null;
    }

    console.log("values: ", values)

    // console.log("final: ", rest);
    // return;
    setLoading(true);
    const res = await createBerth(rest, user?.token);
    // console.log("res: ", res);

    if (res?.status === "Success") {
      toast.success("Berth listed successfully");

      // Track berth listing created
      trackBerthListingCreated(res?.data?.id, rest.availableFor, rest.location);

      setLoading(false);
      await uploadImage(res?.data?.id);
      return;
    }

    if (res?.status === false) {
      if (res?.message === "Invalid or expired access token.") {
        toast.error("Token expired, please re-login and try");
        setLoading(false);
        return;
      }

      toast.error("Something went wrong please try later");
      setLoading(false);
      return;
    }

    toast.error("Something went wrong please try later");
    setLoading(false);
    return;
  };

  const handleDeleteBerth = (id) => {
    setBerths((berths) => berths.filter((berth) => berth?.id !== id));
    return;
  };

  const handleEditBerth = (data) => {
    // console.log("edit data: ", data);
    const { berthSpecifications, berthImagePath, availableDate, availableFromDate, ...rest } =
      data;
    setImage(berthImagePath ? `${baseUrl}${berthImagePath}` : null);
    const { length, beam, depth, dockHeight } = berthSpecifications;
    rest.length = length;
    rest.beam = beam;
    rest.depth = depth;
    rest.dockHeight = dockHeight;

    // Check if power is a custom value
    if (rest.power && !powers.includes(rest.power)) {
      setShowOtherPowerTextField(true);
      setUserSpecifiedPower(rest.power);
      rest.power = "Other , please specify";
    } else {
      setShowOtherPowerTextField(false);
      setUserSpecifiedPower("");
    }

    const isSale = rest.availableFor?.toLowerCase() === "sale";

    if (isSale) {
      // Sale type: use availableFromDate
      if (availableFromDate) {
        rest.availableFromDate = dayjs(availableFromDate).format("YYYY-MM-DD");
      }
      // For Sale, set chargeType to fixed and rate to fixedTotalPrice
      rest.chargeType = "fixed";
      rest.rate = data.fixedTotalPrice;
    } else {
      // Hire/Swap/Both: use date range
      if (availableDate !== null && availableDate.length > 0) {
        const berthAvailableDate = dayjs(availableDate[0].startDate).format(
          "YYYY-MM-DD"
        );
        const berthAvailableUntil = dayjs(availableDate[0].endDate).format(
          "YYYY-MM-DD"
        );
        rest.berthAvailableDate = berthAvailableDate;
        rest.berthAvailableUntil = berthAvailableUntil;
      }

      // Populate all pricing fields that exist
      if (data.chargesPerDay) {
        rest.chargesPerDay = data.chargesPerDay;
        rest.rate = data.chargesPerDay; // Default display
        rest.chargeType = "day"; // Default UI selection
      }

      if (data.chargesPerMonth) {
        rest.chargesPerMonth = data.chargesPerMonth;
        if (!rest.rate) {
          rest.rate = data.chargesPerMonth;
          rest.chargeType = "month";
        }
      }

      if (data.fixedTotalPrice) {
        rest.fixedTotalPrice = data.fixedTotalPrice;
        if (data.fixedPeriodStartDate) {
          rest.fixedPeriodStartDate = dayjs(data.fixedPeriodStartDate).format(
            "YYYY-MM-DD"
          );
        }
        if (data.fixedPeriodEndDate) {
          rest.fixedPeriodEndDate = dayjs(data.fixedPeriodEndDate).format(
            "YYYY-MM-DD"
          );
        }
        if (!rest.rate) {
          rest.rate = data.fixedTotalPrice;
          rest.chargeType = "fixed";
        }
      }

      // Set default chargeType if none exists
      if (!rest.chargeType) {
        rest.chargeType = "day";
      }
    }

    // Include notes if present
    if (data.notes) {
      rest.notes = data.notes;
    }

    setValues(rest);
    window.scrollTo(0, 0);
    return;
  };

  // console.log("values: ", values);

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!values?.currentLatitude) {
      toast.error("Please select berth location");
      return;
    }

    const isSale = values?.availableFor === "sale";

    // Validation for Sale type
    if (isSale) {
      if (!values?.availableFromDate) {
        toast.error("Please select 'Available from' date");
        return;
      }
      if (!values?.fixedTotalPrice || values?.fixedTotalPrice <= 0) {
        toast.error("Please enter the sale price");
        return;
      }
    } else {
      // Validation for Hire/Swap/Both types
      if (!values?.berthAvailableDate) {
        toast.error("Please select slot of availability");
        return;
      }

      // Check if at least one pricing type has a value greater than 0
      if ((!values?.chargesPerDay || values?.chargesPerDay <= 0) &&
          (!values?.chargesPerMonth || values?.chargesPerMonth <= 0) &&
          (!values?.fixedTotalPrice || values?.fixedTotalPrice <= 0)) {
        toast.error("Please enter at least one rate value greater than 0");
        return;
      }

      // Validate fixed rate period if fixedTotalPrice is set
      if (values.fixedTotalPrice && values.fixedTotalPrice > 0 &&
          (!values.fixedPeriodStartDate || !values.fixedPeriodEndDate)) {
        toast.error("Please select duration of fixed rate period");
        return;
      }

      // Validate monthly pricing date requirements if chargesPerMonth is set
      if (values.chargesPerMonth && values.chargesPerMonth > 0 &&
          values.berthAvailableDate && values.berthAvailableUntil) {
        const startDate = dayjs(values.berthAvailableDate);
        const endDate = dayjs(values.berthAvailableUntil);
        const duration = endDate.diff(startDate, 'day') + 1;

        if (duration < 30) {
          toast.error("Monthly pricing requires at least 30 days. Please update your availability dates or remove monthly pricing.");
          return;
        }
      }
    }

    const {
      beam,
      createdAt,
      depth,
      dockHeight,
      length,
      updatedAt,
      userId,
      berthAvailableUntil,
      berthAvailableDate,
      chargeType,
      rate,
      ...rest
    } = values;

    const berthSpecifications = { length, beam, depth, dockHeight };
    rest.berthSpecifications = berthSpecifications;

    // Handle date differently for Sale vs other types
    if (isSale) {
      rest.availableDate = null;
      rest.availableFromDate = values.availableFromDate;
    } else {
      const dateSlot = [
        { startDate: berthAvailableDate, endDate: berthAvailableUntil },
      ];
      rest.availableDate = dateSlot;
      rest.availableFromDate = null;
    }

    // Use custom power value if "Other" was selected
    if (showOtherPowerTextField && userSpecifiedPower) {
      rest.power = userSpecifiedPower;
    }

    // Convert 0 pricing values to null
    if (!rest.chargesPerDay || rest.chargesPerDay <= 0) {
      rest.chargesPerDay = null;
    }
    if (!rest.chargesPerMonth || rest.chargesPerMonth <= 0) {
      rest.chargesPerMonth = null;
    }
    if (!rest.fixedTotalPrice || rest.fixedTotalPrice <= 0) {
      rest.fixedTotalPrice = null;
    }

    // Only include fixed period dates if fixedTotalPrice has value
    if (rest.fixedTotalPrice && rest.fixedTotalPrice > 0) {
      rest.fixedPeriodStartDate = values.fixedPeriodStartDate;
      rest.fixedPeriodEndDate = values.fixedPeriodEndDate;
    } else {
      // Remove fixed period dates if fixedTotalPrice is not set
      rest.fixedPeriodStartDate = null;
      rest.fixedPeriodEndDate = null;
    }

    // console.log("before update: ", rest);

    // return;
    setLoading(true);
    const res = await updateBerthById(rest, user?.token);

    if (res?.status === "Success") {
      toast.success(res?.message);
      setLoading(false);
      await uploadImage(values?.id);
      return;
    }

    if (res?.status === false) {
      if (res?.message === "Invalid or expired access token.") {
        toast.error("Token expired, please re-login and try");
        setLoading(false);
        return;
      }

      toast.error("Something went wrong please try later");
      setLoading(false);
      return;
    }

    toast.error("Something went wrong please try later");
    setLoading(false);
    return;
  };

  const preventMinus = (e) => {
    // console.log("key: ", e.key);
    if (e.code === "Minus") {
      e.preventDefault();
    }
  };

  return (
    <>
      <Box style={{ marginBottom: '30px' }}>
        <Typography variant="h5" className="page-title">
          Create Bearth
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12} lg={12}>
            <Card>
              <CardContent>
                <form onSubmit={values?.id ? handleUpdate : handleSubmit}>
                  <Typography variant="h4" component="h4" className="card-title">
                    List your own Berth
                  </Typography>

                  {/* input fields container */}
                  <Grid container spacing={3}>
                    {/* image container */}
                    <Grid item xs={12} sm={12} md={4} lg={3}>
                      <div className="blogImageParent">
                        <img
                          className="blogImg"
                          src={image || defaultBlogImage}
                          alt="blogImage"
                        />
                      </div>
                      <input
                        ref={imageRef}
                        className="file-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                      <Button
                        className="btn"
                        style={{ marginTop: '15px' }}
                        variant="contained"
                        color="primary"
                        onClick={() => imageRef.current.click()}
                        endIcon={<AddPhotoAlternateIcon />}
                        // sx={{ marginTop: "10px" }}
                      >
                        Select image
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={12} md={8} lg={9}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={12} md={8} lg={6}>
                          <TextField
                            id="name"
                            name="name"
                            label="Berth name"
                            size="small"
                            fullWidth
                            required
                            autoComplete="name"
                            value={values.name || ""}
                            onChange={(e) => setValue(e.target)}
                          />
                        </Grid>
                       
                        {/* Port Authority Email - Hidden for Sale type */}
                        {values?.availableFor !== "sale" && (
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <TextField
                              required
                              id="portEmail"
                              name="portEmail"
                              label="Port authority's email"
                              size="small"
                              fullWidth
                              type="email"
                              autoComplete="portEmail"
                              value={values.portEmail || ""}
                              onChange={(e) => setValue(e.target)}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <TextField
                            id="length"
                            name="length"
                            label="Length (meters)"
                            size="small"
                            fullWidth
                            required
                            type="number"
                            autoComplete="length"
                            value={values.length || ""}
                            onChange={(e) => setValue(e.target)}
                            onKeyPress={preventMinus}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <TextField
                            id="beam"
                            name="beam"
                            label="Beam (meters)"
                            size="small"
                            fullWidth
                            required
                            type="number"
                            autoComplete="beam"
                            value={values.beam || ""}
                            onChange={(e) => setValue(e.target)}
                            onKeyPress={preventMinus}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <TextField
                            id="depth"
                            name="depth"
                            label="Depth (meters)"
                            size="small"
                            fullWidth
                            required
                            type="number"
                            autoComplete="depth"
                            value={values.depth || ""}
                            onChange={(e) => setValue(e.target)}
                            onKeyPress={preventMinus}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="powerReq">Power</InputLabel>
                            <Select
                              labelId="powerReq"
                              id="power"
                              fullWidth
                              required={values?.id ? false : true}
                              name="power"
                              value={values?.power || ""}
                              onChange={(e) => setValue(e.target)}
                              label="Power"
                            >
                              {powers.map((power) => (
                                <MenuItem
                                  disabled={values?.power === power}
                                  key={power}
                                  value={power}
                                >
                                  {power}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        {showOtherPowerTextField && (
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <TextField
                              required
                              id="userSpecifiedPower"
                              name="userSpecifiedPower"
                              label="Specify power"
                              size="small"
                              fullWidth
                              value={userSpecifiedPower}
                              onChange={(e) => setUserSpecifiedPower(e.target.value)}
                              placeholder="e.g., 200 Amp 3-Phase"
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <TextField
                            required
                            id="dockHeight"
                            name="dockHeight"
                            label="Dock height (meters)"
                            fullWidth
                            size='small'
                            type="number"
                            autoComplete="dockHeight"
                            value={values.dockHeight || ""}
                            onChange={(e) => setValue(e.target)}
                            onKeyPress={preventMinus}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <Button
                            fullWidth
                            variant="outlined"
                            className="locationButton"
                            sx={{ height: "52px", color: "gray", borderColor: "gray" }}
                            endIcon={<AddLocationAltIcon />}
                            onClick={() => setOpen(true)}
                          >
                            {values?.currentLocation?.name
                              ? values?.currentLocation?.name
                              : "location *"}
                          </Button>
                        </Grid>
                        <Grid item xs={12} sm={12} md={8} lg={6}>
                          <FormControl>
                            <FormLabel id="type">Make berth available for</FormLabel>
                            <RadioGroup
                              row
                              aria-labelledby="type"
                              name="availableFor"
                              value={values?.availableFor}
                              onChange={(e) => setValue(e.target)}
                            >
                              <FormControlLabel
                                value="swap"
                                checked={values?.availableFor === "swap"}
                                required
                                control={<Radio />}
                                label="Swap"
                              />
                              <FormControlLabel
                                value="hire"
                                checked={values?.availableFor === "hire"}
                                required
                                control={<Radio />}
                                label="Hire (rent)"
                              />
                              <FormControlLabel
                                value="hire or swap"
                                checked={values?.availableFor === "hire or swap"}
                                required
                                control={<Radio />}
                                label="Both"
                              />
                              <FormControlLabel
                                value="sale"
                                checked={values?.availableFor === "sale"}
                                required
                                control={<Radio />}
                                label="Sale"
                              />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          {values?.availableFor === "sale" ? (
                            // Sale type: Single "Available from" date
                            <TextField
                              required
                              id="availableFromDate"
                              name="availableFromDate"
                              label="Available from"
                              type="date"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                sx: { height: "52px" }
                              }}
                              value={values.availableFromDate || ""}
                              onChange={(e) => setValue(e.target)}
                            />
                          ) : (
                            // Hire/Swap/Both: Date range
                            <Button
                              fullWidth
                              variant="outlined"
                              className="locationButton"
                              sx={{ height: "52px", color: "gray", borderColor: "gray" }}
                              endIcon={<DateRangeIcon />}
                              onClick={() => {
                                setDateModalMode("availability")
                                setDateModalOpen(true)
                              }}
                            >
                              {values?.berthAvailableDate
                                ? `${dayjs(values?.berthAvailableDate).format(
                                    "YYYY-MM-DD"
                                  )} -- ${dayjs(values?.berthAvailableUntil).format(
                                    "YYYY-MM-DD"
                                  )}`
                                : "availability slot *"}
                            </Button>
                          )}
                        </Grid>
                        {/* Notes Field - Available for all listing types */}
                        <Grid item xs={12} sm={12} md={12} lg={12}>
                          <TextField
                            id="notes"
                            name="notes"
                            label="Mooring Info / Additional Notes (Optional)"
                            fullWidth
                            multiline
                            minRows={8}
                            maxRows={12}
                            value={values.notes || ""}
                            onChange={(e) => setValue(e.target)}
                            placeholder="Concession expiry date, power, parking information, facilities, marina services, etc."
                            sx={{
                              '& .MuiInputBase-root': {
                                minHeight: '180px',
                                alignItems: 'flex-start'
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={12} md={10} lg={10} xl={8}>
                          {/* Pricing Type Selection - Hidden for Sale type (only fixed rate) */}
                          {values?.availableFor !== "sale" && (
                            <Grid item xs={12} sm={10} md={9} lg={8} xl={6} style={{ marginBottom: '16px' }}>
                              <Typography variant="subtitle2" style={{ marginBottom: '8px', color: '#666' }}>
                                Pricing Type
                              </Typography>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '8px',
                                padding: '4px',
                                width: '100%',
                                gap: '2px'
                              }}>
                                {['day', 'month', 'fixed'].map((type) => (
                                  <button
                                    key={type}
                                    type="button"
                                    onClick={() => handleRateTypeChange(type)}
                                    style={{
                                      padding: '12px 8px',
                                      border: 'none',
                                      backgroundColor: values.chargeType === type ? '#000' : 'transparent',
                                      cursor: 'pointer',
                                      borderRadius: '6px',
                                      fontWeight: '500',
                                      fontSize: '14px',
                                      color: values.chargeType === type ? '#fff' : '#666',
                                      transition: 'all 0.2s ease',
                                      textAlign: 'center',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {type === 'day' ? 'Per Day' : type === 'month' ? 'Per Month' : 'Fixed Rate'}
                                  </button>
                                ))}
                              </div>
                            </Grid>
                          )}

                          {/* Rate Input and Calendar Row */}
                          <Grid container spacing={2}>
                            {values?.availableFor === "sale" ? (
                              // Sale type: Only sale price input (no period selection)
                              <Grid item xs={12} sm={12} md={8} lg={6} xl={5}>
                                <TextField
                                  required
                                  id="salePrice"
                                  name="fixedTotalPrice"
                                  label="Sale Price excluding Tax (€)"
                                  size="small"
                                  fullWidth
                                  type="number"
                                  autoComplete="salePrice"
                                  InputLabelProps={{
                                    shrink: true,
                                    style: { whiteSpace: 'nowrap', overflow: 'visible' }
                                  }}
                                  onChange={(e) => {
                                    setValues({
                                      ...values,
                                      fixedTotalPrice: e.target.value,
                                      rate: e.target.value,
                                      chargeType: 'fixed'
                                    });
                                  }}
                                  onKeyPress={preventMinus}
                                  value={values?.fixedTotalPrice || ""}
                                />
                              </Grid>
                            ) : (
                              // Hire/Swap/Both: Rate input based on chargeType
                              <>
                                <Grid item xs={12} sm={values.chargeType === 'fixed' ? 6 : 8} md={values.chargeType === 'fixed' ? 5 : 6} lg={values.chargeType === 'fixed' ? 4 : 5} xl={values.chargeType === 'fixed' ? 3 : 4}>
                                  <TextField
                                    required
                                    id={`${values.chargeType}Rate`}
                                    name={`${values.chargeType}Rate`}
                                    label={`Rate${values.chargeType === 'day' ? ' per day' :
                                            values.chargeType === 'month' ? ' per month' :
                                            ' (fixed for selected period)'} (€)`}
                                    size="small"
                                    fullWidth
                                    type="number"
                                    autoComplete={`${values.chargeType}Rate`}
                                    onChange={handleRate}
                                    onKeyPress={preventMinus}
                                    value={values?.rate}
                                  />
                                </Grid>

                                {values.chargeType === 'fixed' && (
                                  <Grid item xs={12} sm={6} md={7} lg={6} xl={5}>
                                    <Button
                                      fullWidth
                                      variant="outlined"
                                      className="locationButton"
                                      sx={{ height: "40px", color: "gray", borderColor: "gray" }}
                                      endIcon={<DateRangeIcon />}
                                      onClick={() => {
                                        setDateModalMode("fixedPrice")
                                        setDateModalOpen(true)
                                      }}
                                    >
                                      {values?.fixedPeriodStartDate
                                        ? `${dayjs(values?.fixedPeriodStartDate).format("YYYY-MM-DD")} -- ${dayjs(values?.fixedPeriodEndDate).format("YYYY-MM-DD")}`
                                        : "Select period for fixed rate *"}
                                    </Button>
                                  </Grid>
                                )}
                              </>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item size={{ xs: 12, md: 12, lg: 12 }}>
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={!profileComplete || !isStripeAccountVerified}
                        // sx={{ mt: 2 }}
                        color="primary"
                        className="btn"
                        loading={loading}
                        loadingPosition="end"
                      >
                        {values?.id ? "update" : "Submit"}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      <Box style={{ marginBottom: '30px' }}>
        <Typography variant="h5" className="page-title">
          Your Berths
        </Typography>
        <Grid container spacing={3}>
          {berths === null ? (
            <CardSkeleton />
          ) : berths?.length === 0 ? (
            <Grid item xs={12}>
              <Card sx={{ width: '100%', height: '150px' }}>
                <CardContent sx={{ height: "100%", display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <h3 style={{ textAlign: 'center' }}>Berths not available!</h3>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            berths?.map((berth) => {
              return (
                <Grid item xs={12} sm={6} md={4} lg={3}>
                <BerthCard
                  type="own"
                  data={berth}
                  token={user?.token}
                  deleteBerth={handleDeleteBerth}
                  editBerth={handleEditBerth}
                />
                </Grid>
              );
            })
          )}
        </Grid>
      </Box>

      {/* location search modal */}
      <BerthLocationModal
        open={open}
        handleClose={() => setOpen(false)}
        handleFinalPlace={handleSelectedPlace}
      />
      {/* berth date slot booking modal */}
      <BerthDateSlotModal
        open={dateModalOpen}
        handleClose={() => setDateModalOpen(false)}
        handleDateSlotSelection={handleDateSlotSelection}
        title={dateModalMode === "availability" ? "Select berth availability period" : "Select fixed price period"}
        mode={dateModalMode}
        values={values}
        chargeType={values.chargeType}
      />
    </>
  );
};
